import { Box } from "@mui/material";
import { DateTime } from "luxon";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { TEXT_FIELD_WIDTH } from "../../../../theme/tokens";
import { DateInput } from "../../../UIToolkit/Inputs/DateInput";
import { SystemTimePicker } from "../../../UIToolkit/Inputs/SystemTimePicker";
import { EyebrowLabel } from "../../../UIToolkit/molecules/EyebrowLabel";

type Props = {
	callInTime: DateTime;
	onCallInTimeChange: (next: DateTime) => void;
	testIdPrefix: string;
};

/**
 * When the person called in — shown exactly when the wrapped hook sends a call-in time (the reason is the Call-In label
 * itself). A date and a clock, each setting its half of the same instant.
 */
export const GapsRemovalCallInTime: FC<Props> = ({ callInTime, onCallInTimeChange, testIdPrefix }) => {
	const { t } = useTranslation();
	return (
		<Box data-testid={`${testIdPrefix}-call-in-time`}>
			<EyebrowLabel sx={{ mb: 0.75 }}>{t("gaps.callIn.removal.callInTime")}</EyebrowLabel>
			{/* Side by side where they fit, stacked on the narrowest phones: a date field has an intrinsic width it will not
			    shrink below, so a fixed row would overflow at 320px. */}
			<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "flex-start", "& > *": { flex: `1 1 ${TEXT_FIELD_WIDTH.PAIRED_MIN}px`, minWidth: 0 } }}>
				<DateInput
					label={t("gaps.callIn.removal.callInDate")}
					value={callInTime}
					maxDate={DateTime.now()}
					onChange={(day) => onCallInTimeChange(callInTime.set({ year: day.year, month: day.month, day: day.day }))}
					dataTestId={`${testIdPrefix}-call-in-date`}
				/>
				<SystemTimePicker
					label={t("gaps.callIn.removal.callInClock")}
					value={callInTime}
					minutesStep={1}
					onChange={(time) => {
						if (time?.isValid) onCallInTimeChange(callInTime.set({ hour: time.hour, minute: time.minute }));
					}}
					dataTestId={`${testIdPrefix}-call-in-clock`}
				/>
			</Box>
		</Box>
	);
};
